import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Accounting from '../../../../component/accounting';
import AppLayout from '../../../../component/appLayout';
import { Group, MilestoneList, SortableWrapper } from './styles';
import ArchiveMilestoneList from './archiveMilestoneList';
import {
  deleteMilestone,
  getArchiveMilestoneList,
  getMilestoneList,
  updateMilestone
} from '../../../../services/projectServices';
import { useParams } from 'react-router-dom';
import MilestoneLoading from '../../../../component/loading/milestoneLoading';
import isEmpty from 'lodash/isEmpty';
import { getTaskGroupList, updateTaskDetails } from '../../../../services/taskServices';
import { captureException } from '../../../../services/logService';
import {
  DndContext,
  DragOverlay,
  DragStartEvent,
  DragEndEvent,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors
} from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import MilestoneItem from './milestoneItem';
import { rootStore } from '../../../../mobx/rootStore';
import { TaskModel } from '../../../../mobx/models/task';
import { checkSubtaskStatus, dispatchTaskUpdates, onUpdateStatus } from '../../../../services/taskActionService';
import { StatusListInterface } from '../../../../interfaces/ProjectInterface';
import { EMPTY_STATE_SIZE, STATUS_TYPE } from '../../../../global/constants';
import { observer } from 'mobx-react-lite';
import { Milestone as MilestoneType } from '../../../../mobx/interfaces/project';
import BottomSubNavigation from '../../../../component/bottomNavigation/subNavigation';
import BottomNavigationItem from '../../../../component/bottomNavigation/subNavigation/bottomNavigationItem';
import EmptyStateV2 from '../../../../component/emptyState';
import MilestoneModals from './milestoneModals';

function findOldestCreatedOnDate(tasks: TaskModel[]): string | null {
  let oldestDate: string | null = null;
  tasks.forEach((task) => {
    if (!oldestDate || (task?.createdOn && task?.createdOn.toISOString() < oldestDate)) {
      oldestDate = task.createdOn.toISOString();
    }
  });
  return oldestDate;
}

function findNewestDueDate(tasks: TaskModel[]): string | null {
  let newestDate: string | null = null;
  tasks.forEach((task) => {
    if (!newestDate || (task?.dueDate && task?.dueDate?.toISOString() > newestDate)) {
      newestDate = task.dueDate?.toISOString() || null;
    }
  });
  return newestDate;
}

const noop = () => {};

const MilestoneDragPreview: React.FC<{ item: MilestoneType }> = ({ item }) => {
  const { newOldestDate, newestDueDate } = useMemo(() => {
    return {
      newOldestDate: findOldestCreatedOnDate(item?.tasks),
      newestDueDate: findNewestDueDate(item?.tasks)
    };
  }, [item?.tasks]);

  return (
    <Group isEmptyState={false} style={{ pointerEvents: 'none' }}>
      <MilestoneItem
        item={item}
        isMilestoneItem={true}
        loadData={noop}
        milestoneIndex={0}
        newOldestDate={newOldestDate}
        newestDueDate={newestDueDate}
        closeArchiveModal={noop}
        selectedId=''
        onChangeStatus={noop}
        onClickTaskCheckBox={noop}
        onOpenDeleteModal={noop}
        onOpenArchiveModal={noop}
        onOpenEditModal={noop}
        onOpenCreateTask={noop}
        onOpenDeleteTask={noop}
        onOpenTaskModal={noop}
        onToggleCompletedTask={noop}
        onSetButtonLoading={noop}
        isSortableItem={true}
      />
    </Group>
  );
};

type SortableMilestoneItemProps = Omit<
  React.ComponentProps<typeof MilestoneItem>,
  'isMilestoneItem' | 'isSortableItem' | 'sortableAttributes' | 'sortableListeners'
>;

const SortableMilestoneItem: React.FC<SortableMilestoneItemProps> = ({ item, ...rest }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: item.id });
  const cssTransform = CSS.Translate.toString(transform ? { ...transform } : null) || undefined;

  return (
    <SortableWrapper
      ref={setNodeRef}
      isDragging={isDragging}
      cssTransform={cssTransform}
      cssTransition={transition || undefined}>
      <MilestoneItem
        item={item}
        isMilestoneItem={true}
        isSortableItem={true}
        sortableAttributes={attributes}
        sortableListeners={listeners}
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...rest}
      />
    </SortableWrapper>
  );
};

const Milestone: React.FC = () => {
  // States
  const [deleteModelOpen, setDeleteModelOpen] = useState(false);
  const [archiveModelOpen, setArchiveModelOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [buttonLoading, setButtonLoading] = useState(false);
  const [isOpenMilestoneModal, setIsOpenMilestoneModal] = useState(false);
  const [selectedId, setSelectedId] = useState('');
  const [open, setOpen] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const [taskDeleteModelOpen, setTaskDeleteModelOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<TaskModel>();
  const [deleteLoading, setDeleteLoading] = useState<boolean>();
  const [confirmationModelOpen, setConfirmationModelOpen] = useState<boolean>(false);
  const [confirmationLoading, setConfirmationLoading] = useState<boolean>(false);
  const [status, setStatus] = useState<StatusListInterface>();
  const [previousStatus, setPreviousStatus] = useState<StatusListInterface>();
  const [currentTask, setCurrentTask] = useState<TaskModel>();
  const [selectedMilestone, setSelectedMilestone] = useState<MilestoneType | null>(null);
  const [activeId, setActiveId] = useState<string | null>(null);

  // Mobx store variables
  const { projectStore, workspaceStore } = rootStore;
  const { currentProject } = projectStore;
  const { milestones, archiveMilestoneList } = currentProject || {};
  const { currentWorkspace: workspace } = workspaceStore;

  // Other variables
  const params: { id: string } = useParams();
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));
  const sortableIds = useMemo(() => milestones?.map((milestone) => milestone.id) || [], [milestones]);

  // load initial data
  const loadData = useCallback(async () => {
    try {
      if (!isEmpty(workspace?.id)) {
        setLoading(true);
        const promise = [];
        promise.push(getMilestoneList(params?.id, false, true));
        promise.push(getArchiveMilestoneList(params?.id));
        promise.push(getTaskGroupList(currentProject?.id, false, false));
        await Promise.all(promise);
      }
    } catch (error) {
      captureException(error);
      setLoading(false);
    } finally {
      setLoading(false);
    }
  }, [currentProject?.id, params?.id, workspace?.id]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    if (!activeId) return undefined;

    const originalCursor = document.documentElement.style.cursor;
    document.documentElement.style.cursor = 'grabbing';

    return () => {
      document.documentElement.style.cursor = originalCursor;
    };
  }, [activeId]);

  // close delete modal
  const closeDeleteModal = useCallback(() => {
    setSelectedId('');
    setDeleteModelOpen(false);
  }, []);

  // close archive modal
  const closeArchiveModal = useCallback(() => {
    setSelectedId('');
    setArchiveModelOpen(false);
  }, []);

  // open milestone modal
  const openMilestoneModal = useCallback(() => {
    setIsOpenMilestoneModal(true);
  }, []);

  // close milestone modal
  const closeMilestoneModal = useCallback(() => {
    setIsOpenMilestoneModal(false);
    setSelectedMilestone(null);
  }, []);

  // delete milestone
  const onDeleteMilestone = useCallback(async () => {
    try {
      setButtonLoading(true);
      const result = await deleteMilestone(selectedId);
      if (result) {
        closeDeleteModal();
        loadData();
      }
    } catch (error) {
      captureException(error);
    } finally {
      setButtonLoading(false);
    }
  }, [closeDeleteModal, loadData, selectedId]);

  // archive milestone
  const onArchiveMilestone = useCallback(async () => {
    try {
      setButtonLoading(true);
      const payload = {
        milestoneId: selectedId,
        isArchived: true
      };
      const result = await updateMilestone(payload);
      if (result) {
        rootStore.projectStore.currentProject?.archiveMilestone(selectedId);
        closeArchiveModal();
        loadData();
      }
    } catch (error) {
      captureException(error);
    } finally {
      setButtonLoading(false);
    }
  }, [closeArchiveModal, loadData, selectedId]);

  // handle cancel
  const handleCancel = useCallback(() => {
    setOpen(false);
    setSelectedId('');
  }, []);

  // Calculate updated index for milestone ordering
  const calculateUpdatedMilestoneIndex = useCallback(
    (destinationIndex: number, sourceIndex: number) => {
      let updatedMilestoneIndex;
      if (destinationIndex === 0) {
        const nextMilestoneIndex = milestones?.[0]?.index;
        updatedMilestoneIndex = nextMilestoneIndex ? nextMilestoneIndex - 50 : new Date().getTime();
      } else if (milestones && destinationIndex === milestones?.length - 1) {
        const previousMilestoneIndex = milestones?.[milestones?.length - 1]?.index;
        updatedMilestoneIndex = previousMilestoneIndex ? previousMilestoneIndex + 50 : new Date().getTime();
      } else {
        if (destinationIndex < sourceIndex) {
          const previousMilestoneIndex = milestones?.[destinationIndex - 1]?.index || 0;
          const nextMilestoneIndex = milestones?.[destinationIndex]?.index || 0;
          updatedMilestoneIndex = (previousMilestoneIndex + nextMilestoneIndex) / 2;
        } else {
          const previousMilestoneIndex: number = milestones?.[destinationIndex]?.index || 0;
          const nextMilestoneIndex: number = milestones?.[destinationIndex + 1]?.index || 0;
          updatedMilestoneIndex = (previousMilestoneIndex + nextMilestoneIndex) / 2;
        }
      }
      return updatedMilestoneIndex;
    },
    [milestones]
  );

  // Handle drag start
  const handleDragStart = useCallback((event: DragStartEvent) => {
    setActiveId(event.active.id as string);
  }, []);

  // logic for drag end
  const handleDragEnd = useCallback(
    async (event: DragEndEvent) => {
      try {
        const { active, over } = event;
        if (!over || active.id === over.id) {
          setActiveId(null);
          return;
        }

        const sourceIndex = milestones?.findIndex((milestone) => milestone.id === active.id);
        const destinationIndex = milestones?.findIndex((milestone) => milestone.id === over.id);

        if (
          sourceIndex === undefined ||
          destinationIndex === undefined ||
          sourceIndex === -1 ||
          destinationIndex === -1
        ) {
          setActiveId(null);
          return;
        }

        const milestoneId = active.id as string;
        const selectedMilestone = milestones?.find((milestone) => milestone?.id === milestoneId);

        if (!selectedMilestone) {
          setActiveId(null);
          return;
        }

        const updatedMilestoneIndex = calculateUpdatedMilestoneIndex(destinationIndex, sourceIndex);
        const updatedMilestone = { ...selectedMilestone, index: updatedMilestoneIndex };

        // Update the milestone using ProjectModel's action
        if (rootStore.projectStore.currentProject) {
          rootStore.projectStore.currentProject.updateMilestone(milestoneId, updatedMilestone);
        }

        try {
          const payload = {
            milestoneId: milestoneId,
            index: updatedMilestoneIndex
          };
          await updateMilestone(payload, false);
        } catch (error) {
          captureException(error);
        }
      } catch (error) {
        captureException(error);
      } finally {
        setActiveId(null);
      }
    },
    [milestones, calculateUpdatedMilestoneIndex]
  );

  // handle toggle for archive milestone
  const handleToggle = useCallback(() => {
    setShowArchived((prev) => !prev);
  }, []);

  // close delete modal
  const onDeleteModalClose = useCallback(() => {
    setTaskDeleteModelOpen(false);
  }, []);

  // delete task
  const deleteTask = useCallback(async (task: TaskModel) => {
    try {
      setDeleteLoading(true);

      const payloadTask = { isArchived: true, projectId: task?.projectId };
      const response = await updateTaskDetails(task?.id, payloadTask);

      if (response) {
        rootStore.taskStore.removeTasks([task.id]);
        rootStore.projectStore.currentProject?.setMilestonesList(rootStore.projectStore.currentProject.milestones);
      }
    } catch (e) {
      captureException(e);
    } finally {
      setDeleteLoading(false);
      setTaskDeleteModelOpen(false);
    }
  }, []);

  // for open confirmation model
  const handleOpenConfirmationModal = useCallback(() => {
    setConfirmationModelOpen(true);
  }, []);

  // for close confirmation model
  const closeConfirmationModal = useCallback(() => {
    setConfirmationModelOpen(false);
  }, []);

  const onChangeStatus = useCallback(
    (task: TaskModel, status: StatusListInterface, previousStatus: StatusListInterface) => {
      const isSubtaskRemaining = checkSubtaskStatus(task);
      if (status.Type === STATUS_TYPE.COMPLETED && !isSubtaskRemaining) {
        handleOpenConfirmationModal();
        setStatus(status);
        setPreviousStatus(previousStatus);
        setCurrentTask(task);
        return;
      }
      onUpdateStatus(task, status, previousStatus, false);
    },
    [handleOpenConfirmationModal]
  );

  //Handle checkbox click when project status is disabled
  const onClickTaskCheckBox = useCallback(
    (task: TaskModel, status: boolean) => {
      const isSubtaskRemaining = checkSubtaskStatus(task);
      if (status && !isSubtaskRemaining) {
        handleOpenConfirmationModal();
        setCurrentTask(task);
        return;
      }
      task.setStatus(status, false);
      dispatchTaskUpdates(task, status);
    },
    [handleOpenConfirmationModal]
  );

  // Action callbacks for MilestoneItem
  const handleOpenDeleteModal = useCallback((milestoneId: string) => {
    setSelectedId(milestoneId);
    setDeleteModelOpen(true);
  }, []);

  const handleOpenArchiveModal = useCallback((milestoneId: string) => {
    setSelectedId(milestoneId);
    setArchiveModelOpen(true);
  }, []);

  const handleOpenEditModal = useCallback((milestone: MilestoneType) => {
    setIsOpenMilestoneModal(true);
    setSelectedMilestone(milestone);
  }, []);

  const handleOpenCreateTask = useCallback(
    (milestone: MilestoneType) => {
      rootStore.taskStore.createTaskInput.setProject(currentProject?.id || '');
      rootStore.taskStore.createTaskInput.setMilestone(milestone?.id || '');
      setOpen(true);
    },
    [currentProject]
  );

  const handleOpenDeleteTask = useCallback((task: TaskModel) => {
    setSelectedItem(task);
    setTaskDeleteModelOpen(true);
  }, []);

  const handleOpenTaskModal = useCallback(() => {
    setOpen(true);
  }, []);

  const handleToggleCompletedTask = useCallback((value: boolean, itemId: string) => {
    if (!value) {
      setSelectedId('');
    }
    setSelectedId(itemId);
  }, []);

  const handleSetButtonLoading = useCallback((value: boolean) => {
    setButtonLoading(value);
  }, []);

  //Handle Yes button click for confirmation model
  const onClickConfirm = useCallback(() => {
    if (!currentTask) return;
    try {
      setConfirmationLoading(true);
      if (currentTask.project?.statusEnable && status && previousStatus) {
        onUpdateStatus(currentTask, status, previousStatus, true);
      } else {
        currentTask.setStatus(true, true);
        dispatchTaskUpdates(currentTask, true);
      }
    } catch (error) {
      captureException(error);
    } finally {
      setConfirmationLoading(false);
      closeConfirmationModal();
    }
  }, [closeConfirmationModal, currentTask, previousStatus, status]);

  //Handle No button click for confirmation model
  const onClickNo = useCallback(() => {
    if (!currentTask) return;
    try {
      if (currentTask.project?.statusEnable && status && previousStatus) {
        onUpdateStatus(currentTask, status, previousStatus, false);
      } else {
        currentTask.setStatus(true, false);
        dispatchTaskUpdates(currentTask, true);
      }
    } catch (error) {
      captureException(error);
    } finally {
      closeConfirmationModal();
    }
  }, [closeConfirmationModal, currentTask, previousStatus, status]);

  // render milestone list
  const renderMilestoneList = useMemo(() => {
    return (
      milestones &&
      milestones.length > 0 &&
      milestones?.map((item, milestoneIndex) => {
        const newOldestDate = findOldestCreatedOnDate(item?.tasks);
        const newestDueDate = findNewestDueDate(item?.tasks);
        return (
          <SortableMilestoneItem
            key={item.id}
            item={item}
            milestoneIndex={milestoneIndex}
            loadData={loadData}
            newOldestDate={newOldestDate}
            newestDueDate={newestDueDate}
            closeArchiveModal={closeArchiveModal}
            selectedId={selectedId}
            onChangeStatus={onChangeStatus}
            onClickTaskCheckBox={onClickTaskCheckBox}
            onOpenDeleteModal={handleOpenDeleteModal}
            onOpenArchiveModal={handleOpenArchiveModal}
            onOpenEditModal={handleOpenEditModal}
            onOpenCreateTask={handleOpenCreateTask}
            onOpenDeleteTask={handleOpenDeleteTask}
            onOpenTaskModal={handleOpenTaskModal}
            onToggleCompletedTask={handleToggleCompletedTask}
            onSetButtonLoading={handleSetButtonLoading}
          />
        );
      })
    );
  }, [
    closeArchiveModal,
    loadData,
    milestones,
    onChangeStatus,
    onClickTaskCheckBox,
    selectedId,
    handleOpenDeleteModal,
    handleOpenArchiveModal,
    handleOpenEditModal,
    handleOpenCreateTask,
    handleOpenDeleteTask,
    handleOpenTaskModal,
    handleToggleCompletedTask,
    handleSetButtonLoading
  ]);

  // render milestone loading component
  const renderMilestoneLoading = useMemo(() => {
    return loading && milestones && milestones.length === 0 && <MilestoneLoading />;
  }, [loading, milestones]);

  // render empty state
  const renderEmptyState = useMemo(() => {
    return (
      !loading &&
      milestones &&
      milestones.length === 0 && (
        <EmptyStateV2
          size={EMPTY_STATE_SIZE.MEDIUM}
          imageUrl={'milestone_empty'}
          title='No milestones added yet'
          description='You can add New Milestone by clicking the button below'
          buttonTitle='New Milestone'
          onClick={openMilestoneModal}
          isAlignedCenter={true}
        />
      )
    );
  }, [loading, milestones, openMilestoneModal]);

  const activeMilestone = useMemo(
    () => milestones?.find((milestone) => milestone.id === activeId) || null,
    [activeId, milestones]
  );

  return (
    <>
      <AppLayout>
        <Accounting />
        <MilestoneList isEmptyState={!loading && milestones && milestones.length === 0}>
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}>
            <SortableContext items={sortableIds} strategy={verticalListSortingStrategy}>
              <Group isEmptyState={!loading && milestones && milestones.length === 0}>
                {renderMilestoneList}
                {renderMilestoneLoading}
                {renderEmptyState}
              </Group>
            </SortableContext>
            <DragOverlay>{activeMilestone ? <MilestoneDragPreview item={activeMilestone} /> : null}</DragOverlay>
          </DndContext>
          {/* archive milestone list */}
          <ArchiveMilestoneList
            archiveMilestoneList={archiveMilestoneList}
            milestones={milestones}
            showArchived={showArchived}
            handleToggle={handleToggle}
            loadData={loadData}
            closeArchiveModal={closeArchiveModal}
            selectedId={selectedId}
            onChangeStatus={onChangeStatus}
            onClickTaskCheckBox={onClickTaskCheckBox}
            findOldestCreatedOnDate={findOldestCreatedOnDate}
            findNewestDueDate={findNewestDueDate}
            onOpenDeleteModal={handleOpenDeleteModal}
            onOpenArchiveModal={handleOpenArchiveModal}
            onOpenEditModal={handleOpenEditModal}
            onOpenCreateTask={handleOpenCreateTask}
            onOpenDeleteTask={handleOpenDeleteTask}
            onOpenTaskModal={handleOpenTaskModal}
            onToggleCompletedTask={handleToggleCompletedTask}
            onSetButtonLoading={handleSetButtonLoading}
          />
        </MilestoneList>
      </AppLayout>
      {/* Bottom Navigation */}
      <BottomSubNavigation
        rightElement={<BottomNavigationItem iconName='plus-icon' onClick={openMilestoneModal} />}
        backNavigationPath='/home'
      />
      <MilestoneModals
        // Milestone Modal
        isOpenMilestoneModal={isOpenMilestoneModal}
        closeMilestoneModal={closeMilestoneModal}
        projectId={params?.id}
        selectedMilestone={selectedMilestone}
        // Delete Modal
        deleteModelOpen={deleteModelOpen}
        closeDeleteModal={closeDeleteModal}
        onDeleteMilestone={onDeleteMilestone}
        buttonLoading={buttonLoading}
        // Archive Modal
        archiveModelOpen={archiveModelOpen}
        closeArchiveModal={closeArchiveModal}
        onArchiveMilestone={onArchiveMilestone}
        // Create Task Modal
        open={open}
        handleCancel={handleCancel}
        loadData={loadData}
        // Task Delete Modal
        taskDeleteModelOpen={taskDeleteModelOpen}
        onDeleteModalClose={onDeleteModalClose}
        selectedItem={selectedItem}
        deleteTask={deleteTask}
        deleteLoading={deleteLoading}
        // Confirmation Modal
        confirmationModelOpen={confirmationModelOpen}
        closeConfirmationModal={closeConfirmationModal}
        confirmationLoading={confirmationLoading}
        onClickConfirm={onClickConfirm}
        onClickNo={onClickNo}
      />
    </>
  );
};
export default observer(Milestone);
